import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Slide,
  TextField,
  Typography,
} from "@material-ui/core";
import EventBusyRoundedIcon from "@material-ui/icons/EventBusyRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import moment from "moment";
import { APPOINTMENT_CANCELLATION_REASONS } from "../../../../utilities/constants";
import { useStyles } from "./styles";

const OTHER = "OTHER";
const NOTE_MIN_LENGTH = 5;

/**
 * Dialog to capture a mandatory cancellation reason before cancelling an
 * appointment. The reason is stored against the appointment and included in the
 * WhatsApp message sent to the patient, so Confirm stays disabled until a
 * reason is chosen (and a note is given when "Other" is selected).
 *
 * @param open: boolean to open the dialog
 * @param appointment: the appointment being cancelled (for the header summary)
 * @param submitting: disables the actions while the request is in flight
 * @param onClose: function to dismiss without cancelling
 * @param onConfirm: called with {reasonCode, reasonLabel, reasonNote}
 * @returns {JSX.Element}
 * @constructor
 */
const CancelAppointmentDialog = ({
  open,
  appointment,
  submitting = false,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [reasonCode, setReasonCode] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  // Reset whenever the dialog reopens so a previous reason is never reused.
  useEffect(() => {
    if (open) {
      setReasonCode("");
      setReasonNote("");
    }
  }, [open]);

  const noteRequired = reasonCode === OTHER;
  const noteTooShort = reasonNote.trim().length < NOTE_MIN_LENGTH;
  const canConfirm =
    !!reasonCode && !(noteRequired && noteTooShort) && !submitting;

  const handleConfirm = () => {
    if (!canConfirm) return;
    const selected = APPOINTMENT_CANCELLATION_REASONS.find(
      (r) => r.code === reasonCode
    );
    onConfirm({
      reasonCode,
      reasonLabel: selected ? selected.label : reasonCode,
      reasonNote: reasonNote.trim(),
    });
  };

  const patientName = appointment?.Patient
    ? `${appointment.Patient.firstName} ${
        appointment.Patient.lastName || ""
      }`.trim()
    : appointment?.patientName || appointment?.title || "";

  const slot = appointment?.startTime
    ? `${moment(appointment.startTime).format("DD MMM YYYY, hh:mm A")}${
        appointment.endTime
          ? ` - ${moment(appointment.endTime).format("hh:mm A")}`
          : ""
      }`
    : "";

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      TransitionComponent={Slide}
      disableBackdropClick
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <EventBusyRoundedIcon />
        </Box>
        <Box>
          <Typography className={classes.title}>Cancel Appointment</Typography>
          {(patientName || slot) && (
            <Typography className={classes.subtitle}>
              {[patientName, slot].filter(Boolean).join(" • ")}
            </Typography>
          )}
        </Box>
      </Box>

      <DialogContent className={classes.content}>
        <Typography className={classes.warning}>
          The appointment stays on the calendar as cancelled and the slot is
          freed up. This cannot be undone.
        </Typography>

        <TextField
          select
          fullWidth
          required
          size="small"
          variant="outlined"
          label="Reason for cancellation"
          className={classes.field}
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          disabled={submitting}
        >
          {APPOINTMENT_CANCELLATION_REASONS.map((reason) => (
            <MenuItem key={reason.code} value={reason.code}>
              {reason.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          variant="outlined"
          className={classes.field}
          label={noteRequired ? "Please specify the reason *" : "Additional note (optional)"}
          value={reasonNote}
          onChange={(e) => setReasonNote(e.target.value)}
          disabled={submitting}
          error={noteRequired && reasonNote.length > 0 && noteTooShort}
          helperText={
            noteRequired && reasonNote.length > 0 && noteTooShort
              ? `Please enter at least ${NOTE_MIN_LENGTH} characters`
              : noteRequired
              ? "This text is sent to the patient on WhatsApp"
              : "Visible to the clinic only — not sent to the patient"
          }
        />

        <Box className={classes.notifyNote}>
          <WhatsAppIcon className={classes.notifyIcon} />
          <span>
            The patient will be notified on WhatsApp with the reason selected
            above.
          </span>
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
          disabled={submitting}
          className={classes.keepBtn}
        >
          Keep Appointment
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleConfirm}
          disabled={!canConfirm}
          className={classes.confirmBtn}
        >
          {submitting ? "Cancelling…" : "Cancel Appointment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelAppointmentDialog;
